import type { GridRow } from '../contracts/grid-row.type';

/** Linear measures on a linked LRM position, when the host supplies them. */
export interface GridSourceLrmPositionMeasures {
  /** Start measure along the route, or the at-measure for point assets. */
  fromMeasure?: number | null;
  /** End measure along the route; null for point assets. */
  toMeasure?: number | null;
}

/**
 * Source-shaped row returned by a data adapter before the orchestrator
 * flattens attributes into display rows. Internal to grid orchestration only.
 */
export interface GridSourceRow extends GridRow {
  /** Internal numeric key for the row. */
  key?: number;
  /** Host row identifier used for deduplication. */
  id?: string;
  /** Discriminator / entity-type value when present. */
  elementType?: string;
  /** Human-readable display name. */
  displayName?: string;
  /** Public key identifier. */
  publicKey?: string;
  /** Business object status. */
  objectStatus?: string;
  /** Record lifecycle status. */
  recordStatus?: string;
  /** Validity start timestamp. */
  validOn?: string;
  /** Validity end timestamp. */
  validTo?: string;
  /** Record date timestamp. */
  recordDate?: string;
  /** Last editor identity. */
  editor?: string;
  /** Reason for the last edit. */
  editReason?: string;
  /** Comment for the last edit. */
  editComment?: string;
  /** Date affected by the record change. */
  affectedRecordDate?: string;
  /** Linked LRM position identifier. */
  lrmPositionId?: string | number;
  /** Linked LRM position measures when the host query includes them. */
  lrmPosition?: GridSourceLrmPositionMeasures | null;
  /** Start / at measure when provided as a top-level field. */
  fromMeasure?: number | null;
  /** End measure when provided as a top-level field. */
  toMeasure?: number | null;
  /** Nested attribute bag merged into the flattened display row. */
  attributes?: Record<string, unknown>;
  /** Map location used for fly-to and viewport membership checks. */
  representativePoint?: { coordinates: [number, number] };
}

export function asGridSourceRow(row: GridRow): GridSourceRow {
  return row as GridSourceRow;
}
